import {UserOutlined} from '@ant-design/icons';
import {OperatorFilter, TermFilter} from '@dofleini/query-builder';
import {useUser} from '@dofleini/security';
import {users} from '@mep/pae-commons';
import {Dropdown, message} from 'antd';
import Avatar from 'antd/es/avatar/avatar';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {memo, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useMutation, useQueryClient} from 'react-query';

import PersonListCell from '@/components/PersonListCell';
import User from '@/components/User';
import {TableContextProvider} from '@/contexts/tableGridContact';
import useAlerts from '@/hooks/useAlerts';
import {
  CACHE_KEY_LIST_PROPOSAL_REQUEST,
  CACHE_KEY_PROPOSAL_REQUEST
} from '@/modules/proposalRequest/contants/proposalRequest';
import ProposalRequestApiService from '@/modules/proposalRequest/services/ProposalRequestApiService';

import style from './index.less';

const {USER_TYPES} = users;

export const _filters = new OperatorFilter({
  type: 'OR',
  filters: [
    new TermFilter({field: 'userType', value: USER_TYPES.FUNCTIONARY}),
    new TermFilter({field: 'userType', value: USER_TYPES.FUNCTIONARY_ORGANIZATION}),
    new TermFilter({field: 'userType', value: USER_TYPES.ONAT_USER})
  ]
});

const ProposalUserAssigned = ({value, entity, ...rest}) => {
  const {t} = useTranslation('common');
  const [visible, setVisible] = useState();
  const {hasPermission} = useUser();
  const queryClient = useQueryClient();
  const {putErrorCode} = useAlerts();
  const {mutateAsync: updateAssigned, isLoading} = useMutation(ProposalRequestApiService.assignResponsible);
  
  const handleSelect = useCallback(async (value) => {
    await updateAssigned({_id: entity._id, assigned: value}, {
      onSuccess: ({data}) => {
        queryClient.invalidateQueries(CACHE_KEY_LIST_PROPOSAL_REQUEST);
        queryClient.invalidateQueries(CACHE_KEY_PROPOSAL_REQUEST);
        queryClient.invalidateQueries(data?._id);
        message.success(t('responsibleChangeSuccess'));
      },
      onError: (e) => {
        putErrorCode(e);
      },
    });
  }, [entity._id, putErrorCode, queryClient, t, updateAssigned]);
  
  const menu = (
    <div className={classNames('bg-white rounded border', style.containerMenu)}>
      <PersonListCell value={value?.id || value} close={() => setVisible(false)} onSubmit={handleSelect}
        filters={_filters.toQuery()}/>
    </div>);
  
  const canAssign = useMemo(() => {
    return hasPermission(['REQUEST:ASSIGN_RESPONSIBLE', 'REQUEST:WRITE'], true);
  }, [hasPermission]);
  
  return (<TableContextProvider {...rest}>
    {canAssign ? <Dropdown
      arrow={false}
      overlay={menu}
      trigger={['click']}
      visible={visible}
      onVisibleChange={setVisible}
      disabled={isLoading}>
      <span className="ant-dropdown-link cursor-pointer" onClick={e => e.preventDefault()}>
        {value ? <User user={value}/> :
          <div className={classNames('flex flex-row items-center')}>
            <Avatar icon={<UserOutlined/>} size="small"/>
            <div className={'ml-2'}>{t('selectPlaceholder')}</div>
          </div>
        }
      </span>
    </Dropdown> :
      <span className="ant-dropdown-link cursor-pointer" onClick={e => e.preventDefault()}>
        {value ? <User user={value}/> :
          <div className={classNames('flex flex-row items-center')}>
            <Avatar icon={<UserOutlined/>} size="small"/>
            <div className={'ml-2'}>{t('selectPlaceholderNoPermission')}</div>
          </div>
        }
      </span>
    }
  </TableContextProvider>
  );
};

ProposalUserAssigned.propTypes = {
  value: PropTypes.object,
  entity: PropTypes.object,
  onChange: PropTypes.func
};

ProposalUserAssigned.defaultProps = {};

export default memo(ProposalUserAssigned);

/**
 * cellRender
 * */
/**
 * This is not a component,
 * it is a function to render the Status component in a table cell
 * @param {String} value
 * @param {Object} record
 * */
export const renderProposalUserAssignedCell = (value, record) => {
  return (
    <ProposalUserAssigned value={value} entity={record} field={'assigned'}/>
  );
};
